import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Star, Calendar, Clock, MapPin, Users, Award, ThumbsUp, Flag, ChevronLeft, ChevronRight, Edit3, PenTool, X } from 'lucide-react';

interface MediaOwner {
  id: string;
  name: string;
  logo: string;
  bannerImage: string;
  tagline: string;
  rating: number;
  reviewCount: number;
  followers: number;
  itemsSold: number;
  joinedDate: string;
  responseTime: string;
  location: string;
  description: string;
  verified: boolean;
}

interface MediaOwnerFeedbackProps {
  mediaOwner: MediaOwner;
}

// Mock review data - expanded with more reviews and larger images
const mockReviews = [
  {
    id: 1,
    userName: "Marcus Roberts",
    userAvatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
    rating: 5,
    date: "2024-01-15",
    reviewText: "Outstanding digital billboard campaign results! The location visibility was exceptional and our ROI exceeded expectations. Professional service from start to finish. Highly recommend for premium urban advertising.",
    helpful: 42,
    campaignType: "Digital Billboard",
    verified: true,
    images: [
      "https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=400&h=300&fit=crop",
      "https://images.unsplash.com/photo-1516156008625-3a9d6067fab5?w=400&h=300&fit=crop",
      "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400&h=300&fit=crop"
    ]
  },
  {
    id: 2,
    userName: "Sarah Chen",
    userAvatar: "https://images.unsplash.com/photo-1494790108755-2616c36c36b0?w=150&h=150&fit=crop&crop=face",
    rating: 5,
    date: "2024-01-12",
    reviewText: "Incredible transit advertising package! Our brand awareness increased significantly. The team provided excellent support and creative insights. Perfect for reaching commuter audiences.",
    helpful: 38,
    campaignType: "Transit Campaign",
    verified: true,
    images: [
      "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=400&h=300&fit=crop",
      "https://images.unsplash.com/photo-1518005020951-eccb494ad742?w=400&h=300&fit=crop"
    ]
  },
  {
    id: 3,
    userName: "Michael Thompson",
    userAvatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
    rating: 4,
    date: "2024-01-10",
    reviewText: "Great classic billboard placement in high-traffic area. Campaign ran smoothly and customer service was responsive. Good value for the investment and solid results.",
    helpful: 29,
    campaignType: "Classic Billboard",
    verified: true,
    images: [
      "https://images.unsplash.com/photo-1570125909232-eb263c188f7e?w=400&h=300&fit=crop",
      "https://images.unsplash.com/photo-1577495508048-b635879837f1?w=400&h=300&fit=crop",
      "https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=400&h=300&fit=crop"
    ]
  },
  {
    id: 4,
    userName: "Emma Wilson",
    userAvatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
    rating: 5,
    date: "2024-01-08",
    reviewText: "Fantastic package deal with multiple billboard locations! Excellent coverage across the city and great pricing. The campaign management was professional and results-driven.",
    helpful: 35,
    campaignType: "Package Deal",
    verified: true,
    images: [
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=300&fit=crop",
      "https://images.unsplash.com/photo-1551434678-e076c223a692?w=400&h=300&fit=crop"
    ]
  },
  {
    id: 5,
    userName: "David Rodriguez",
    userAvatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face",
    rating: 4,
    date: "2024-01-05",
    reviewText: "Professional digital advertising service with excellent visibility. The billboard quality and location exceeded our expectations. Great partner for outdoor advertising campaigns.",
    helpful: 31,
    campaignType: "Digital Billboard",
    verified: true,
    images: [
      "https://images.unsplash.com/photo-1566041510394-cf7c8fe21800?w=400&h=300&fit=crop",
      "https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?w=400&h=300&fit=crop",
      "https://images.unsplash.com/photo-1551076805-e1869033e561?w=400&h=300&fit=crop"
    ]
  },
  {
    id: 6,
    userName: "Jessica Park",
    userAvatar: "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=150&h=150&fit=crop&crop=face",
    rating: 5,
    date: "2024-01-03",
    reviewText: "Amazing results from our highway billboard campaign! High-impact location with excellent visibility. The installation process was smooth and professional throughout.",
    helpful: 27,
    campaignType: "Highway Billboard",
    verified: true,
    images: [
      "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400&h=300&fit=crop",
      "https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=400&h=300&fit=crop"
    ]
  },
  {
    id: 7,
    userName: "Alex Kumar",
    userAvatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
    rating: 4,
    date: "2024-01-01",
    reviewText: "Solid classic billboard campaign with good reach. The team was helpful and responsive. Good pricing and reliable service for traditional outdoor advertising needs.",
    helpful: 23,
    campaignType: "Classic Billboard",
    verified: false,
    images: [
      "https://images.unsplash.com/photo-1556155092-490a1ba16284?w=400&h=300&fit=crop"
    ]
  },
  {
    id: 8,
    userName: "Rachel Green",
    userAvatar: "https://images.unsplash.com/photo-1494790108755-2616c36c36b0?w=150&h=150&fit=crop&crop=face",
    rating: 5,
    date: "2023-12-28",
    reviewText: "Exceptional transit advertising package! Perfect for reaching our target demographic during rush hours. Professional execution and measurable results. Will definitely use again.",
    helpful: 44,
    campaignType: "Transit Campaign",
    verified: true,
    images: [
      "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop",
      "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=300&fit=crop",
      "https://images.unsplash.com/photo-1487017159836-4e23ece2e4cf?w=400&h=300&fit=crop"
    ]
  },
  {
    id: 9,
    userName: "Mark Stevens",
    userAvatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
    rating: 4,
    date: "2023-12-25",
    reviewText: "Great digital billboard placement in prime CBD location. High visibility and excellent customer service. The campaign delivered strong brand awareness for our product launch.",
    helpful: 19,
    campaignType: "Digital Billboard",
    verified: true,
    images: [
      "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=400&h=300&fit=crop",
      "https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=400&h=300&fit=crop"
    ]
  },
  {
    id: 10,
    userName: "Linda Martinez",
    userAvatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
    rating: 5,
    date: "2023-12-22",
    reviewText: "Outstanding package deal with multiple locations! Comprehensive coverage and excellent value. The campaign management team was professional and results exceeded our expectations.",
    helpful: 37,
    campaignType: "Package Deal",
    verified: true,
    images: [
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=300&fit=crop",
      "https://images.unsplash.com/photo-1551434678-e076c223a692?w=400&h=300&fit=crop",
      "https://images.unsplash.com/photo-1556155092-490a1ba16284?w=400&h=300&fit=crop",
      "https://images.unsplash.com/photo-1566041510394-cf7c8fe21800?w=400&h=300&fit=crop"
    ]
  }
];

// Mock rating distribution
const ratingDistribution = [
  { stars: 5, count: 1654, percentage: 83 },
  { stars: 4, count: 248, percentage: 12 },
  { stars: 3, count: 62, percentage: 3 },
  { stars: 2, count: 15, percentage: 1 },
  { stars: 1, count: 5, percentage: 1 }
];

export function MediaOwnerFeedback({ mediaOwner }: MediaOwnerFeedbackProps) {
  const [reviews, setReviews] = useState(mockReviews);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedReviewImages, setSelectedReviewImages] = useState<string[]>([]);
  const [showImageModal, setShowImageModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [showWriteReview, setShowWriteReview] = useState(false);
  const [newReview, setNewReview] = useState({
    userName: '',
    rating: 5,
    reviewText: '',
    campaignType: 'Digital Billboard'
  });
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);

  // Pagination logic
  const totalPages = Math.ceil(reviews.length / itemsPerPage);
  const currentReviews = reviews.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSubmitReview = () => {
    if (newReview.userName.trim() && newReview.reviewText.trim()) {
      const review = {
        id: Date.now(),
        userName: newReview.userName,
        userAvatar: `https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face`,
        rating: newReview.rating,
        date: new Date().toISOString().split('T')[0],
        reviewText: newReview.reviewText,
        helpful: 0,
        campaignType: newReview.campaignType,
        verified: false,
        images: uploadedImages
      };
      
      setReviews([review, ...reviews]);
      setNewReview({
        userName: '',
        rating: 5,
        reviewText: '',
        campaignType: 'Digital Billboard'
      });
      setUploadedImages([]);
      setShowWriteReview(false);
      setCurrentPage(1);
    }
  };

  const renderStars = (rating: number, size: 'sm' | 'md' | 'lg' = 'sm') => {
    const sizeClass = {
      sm: 'h-4 w-4',
      md: 'h-5 w-5', 
      lg: 'h-6 w-6'
    }[size];
    
    return (
      <div className="flex">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${sizeClass} ${
              star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  const openImageModal = (images: string[], startIndex: number = 0) => {
    setSelectedReviewImages(images);
    setSelectedImageIndex(startIndex);
    setShowImageModal(true);
  };

  const nextImage = () => {
    setSelectedImageIndex((prev) => 
      prev === selectedReviewImages.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setSelectedImageIndex((prev) => 
      prev === 0 ? selectedReviewImages.length - 1 : prev - 1
    );
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    Array.from(files).forEach(file => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const result = e.target?.result as string;
          if (result) {
            setUploadedImages(prev => [...prev, result]);
          }
        };
        reader.readAsDataURL(file);
      }
    });
  };

  const removeImage = (index: number) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-8">
      {/* About Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="h-5 w-5 text-emerald-600" />
            About {mediaOwner.name}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Company Description */}
          <div>
            <p className="text-gray-700 leading-relaxed mb-6">
              Billboards are large outdoor advertising displays that deliver exposure to your audience 24 hours a day, 7 days a week. 
              Lamar offers billboard inventory across the U.S. in a variety of sizes and formats. According to Nielsen, nearly 80% of 
              consumers have noticed a billboard advertisement in the past month.
            </p>
          </div>

          {/* Company Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-emerald-50 rounded-lg">
              <Calendar className="h-6 w-6 text-emerald-600 mx-auto mb-2" />
              <div className="text-lg font-bold text-gray-900">Joined {mediaOwner.joinedDate}</div>
              <div className="text-sm text-gray-600">Member since</div>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <Clock className="h-6 w-6 text-blue-600 mx-auto mb-2" />
              <div className="text-lg font-bold text-gray-900">24 hours</div>
              <div className="text-sm text-gray-600">Response time</div>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <Users className="h-6 w-6 text-purple-600 mx-auto mb-2" />
              <div className="text-lg font-bold text-gray-900">{mediaOwner.followers.toLocaleString()}</div>
              <div className="text-sm text-gray-600">Followers</div>
            </div>
            <div className="text-center p-4 bg-orange-50 rounded-lg">
              <MapPin className="h-6 w-6 text-orange-600 mx-auto mb-2" />
              <div className="text-lg font-bold text-gray-900">{mediaOwner.location}</div>
              <div className="text-sm text-gray-600">Location</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Customer Reviews Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Customer Reviews</span>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <Star className="h-5 w-5 text-yellow-400 fill-current" />
                <span className="text-xl font-bold">{mediaOwner.rating}</span>
                <span className="text-gray-600">out of 5</span>
              </div>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Rating Overview */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pb-6 border-b">
            {/* Left: Overall Rating */}
            <div className="text-center lg:text-left">
              <div className="flex items-center justify-center lg:justify-start gap-2 mb-2">
                {renderStars(mediaOwner.rating, 'lg')}
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-1">{mediaOwner.rating} out of 5</div>
              <div className="text-gray-600">{mediaOwner.reviewCount.toLocaleString()} customer reviews</div>
            </div>
            
            {/* Right: Rating Distribution */}
            <div className="space-y-2">
              {ratingDistribution.map((item) => (
                <div key={item.stars} className="flex items-center gap-3">
                  <span className="text-sm font-medium w-6">{item.stars}</span>
                  <Star className="h-4 w-4 text-yellow-400 fill-current" />
                  <Progress value={item.percentage} className="flex-1 h-2" />
                  <span className="text-sm text-gray-600 w-8">{item.percentage}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* Reviews List */}
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Customer Reviews ({reviews.length})</h3>
              <Dialog open={showWriteReview} onOpenChange={setShowWriteReview}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm">
                    <PenTool className="h-4 w-4 mr-2" />
                    Write a Review
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[425px]">
                  <DialogHeader>
                    <DialogTitle>Write a Review</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div>
                      <Label htmlFor="name">Your Name</Label>
                      <Input
                        id="name"
                        value={newReview.userName}
                        onChange={(e) => setNewReview({...newReview, userName: e.target.value})}
                        placeholder="Enter your name"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="rating">Rating</Label>
                      <Select value={newReview.rating.toString()} onValueChange={(value) => setNewReview({...newReview, rating: Number(value)})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 Star</SelectItem>
                          <SelectItem value="2">2 Stars</SelectItem>
                          <SelectItem value="3">3 Stars</SelectItem>
                          <SelectItem value="4">4 Stars</SelectItem>
                          <SelectItem value="5">5 Stars</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor="campaign">Campaign Type</Label>
                      <Select value={newReview.campaignType} onValueChange={(value) => setNewReview({...newReview, campaignType: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Digital Billboard">Digital Billboard</SelectItem>
                          <SelectItem value="Classic Billboard">Classic Billboard</SelectItem>
                          <SelectItem value="Transit Campaign">Transit Campaign</SelectItem>
                          <SelectItem value="Package Deal">Package Deal</SelectItem>
                          <SelectItem value="Highway Billboard">Highway Billboard</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor="review">Your Review</Label>
                      <Textarea
                        id="review"
                        value={newReview.reviewText}
                        onChange={(e) => setNewReview({...newReview, reviewText: e.target.value})}
                        placeholder="Share your experience..."
                        rows={4}
                      />
                    </div>

                    {/* Image Upload Section */}
                    <div>
                      <Label>Add Photos (Optional)</Label>
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleImageUpload}
                            className="file:mr-2 file:px-2 file:py-1 file:border-0 file:bg-emerald-50 file:text-emerald-700 file:rounded-md file:text-sm file:font-medium hover:file:bg-emerald-100"
                          />
                        </div>
                        
                        {uploadedImages.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-sm text-gray-600">{uploadedImages.length} image(s) selected</p>
                            <div className="grid grid-cols-2 gap-2">
                              {uploadedImages.map((image, index) => (
                                <div key={index} className="relative group">
                                  <img
                                    src={image}
                                    alt={`Upload ${index + 1}`}
                                    className="w-full h-20 object-cover rounded-md border border-gray-200"
                                  />
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    className="absolute top-1 right-1 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                    onClick={() => removeImage(index)}
                                  >
                                    <X className="h-3 w-3" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex gap-2 pt-4">
                      <Button 
                        onClick={handleSubmitReview}
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                        disabled={!newReview.userName.trim() || !newReview.reviewText.trim()}
                      >
                        Submit Review
                      </Button>
                      <Button variant="outline" onClick={() => setShowWriteReview(false)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            
            {currentReviews.map((review) => (
              <div key={review.id} className="border-b pb-6 last:border-b-0">
                <div className="flex items-start gap-4">
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={review.userAvatar} alt={review.userName} />
                    <AvatarFallback>{review.userName.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                  </Avatar>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-semibold">{review.userName}</h4>
                      {review.verified && (
                        <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                          Verified
                        </Badge>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-4 mb-3">
                      {renderStars(review.rating)}
                      <span className="text-sm text-gray-600">{review.date}</span>
                      <Badge variant="secondary" className="text-xs">
                        {review.campaignType}
                      </Badge>
                    </div>
                    
                    <p className="text-gray-700 mb-4 leading-relaxed">{review.reviewText}</p>
                    
                    {/* Review Images - Larger Size */}
                    {review.images && review.images.length > 0 && (
                      <div className="flex gap-3 mb-4 flex-wrap">
                        {review.images.slice(0, 3).map((image, index) => (
                          <div 
                            key={index}
                            className="relative cursor-pointer group"
                            onClick={() => openImageModal(review.images, index)}
                          >
                            <img
                              src={image}
                              alt={`Review image ${index + 1}`}
                              className="w-32 h-24 object-cover rounded-lg group-hover:opacity-80 transition-opacity shadow-sm"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all rounded-lg"></div>
                          </div>
                        ))}
                        {review.images.length > 3 && (
                          <div 
                            className="w-32 h-24 bg-gray-100 rounded-lg flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors shadow-sm"
                            onClick={() => openImageModal(review.images, 0)}
                          >
                            <span className="text-sm text-gray-600 font-medium">+{review.images.length - 3} more</span>
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <button className="flex items-center gap-1 hover:text-emerald-600 transition-colors">
                        <ThumbsUp className="h-4 w-4" />
                        Helpful ({review.helpful})
                      </button>
                      <button className="hover:text-gray-900 transition-colors">
                        Reply
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-6 border-t">
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Previous
                  </Button>
                  
                  <div className="flex space-x-1">
                    {/* First page */}
                    {currentPage > 3 && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(1)}
                          className="w-8 h-8 p-0"
                        >
                          1
                        </Button>
                        {currentPage > 4 && (
                          <span className="px-2 py-1 text-gray-500">...</span>
                        )}
                      </>
                    )}
                    
                    {/* Page numbers around current page */}
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const startPage = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
                      const page = startPage + i;
                      
                      if (page <= totalPages) {
                        return (
                          <Button
                            key={page}
                            variant={currentPage === page ? "default" : "outline"}
                            size="sm"
                            onClick={() => setCurrentPage(page)}
                            className={`w-8 h-8 p-0 ${
                              currentPage === page ? 'bg-emerald-600 hover:bg-emerald-700' : ''
                            }`}
                          >
                            {page}
                          </Button>
                        );
                      }
                      return null;
                    })}
                    
                    {/* Last page */}
                    {currentPage < totalPages - 2 && (
                      <>
                        {currentPage < totalPages - 3 && (
                          <span className="px-2 py-1 text-gray-500">...</span>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(totalPages)}
                          className="w-8 h-8 p-0"
                        >
                          {totalPages}
                        </Button>
                      </>
                    )}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>

                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <span>
                    Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, reviews.length)} of {reviews.length}
                  </span>
                  <Select value={itemsPerPage.toString()} onValueChange={(value) => {
                    setItemsPerPage(Number(value));
                    setCurrentPage(1);
                  }}>
                    <SelectTrigger className="w-24 h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">5</SelectItem>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="15">15</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Image Modal */}
      {showImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4">
          <div className="relative max-w-4xl max-h-full">
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
            >
              <span className="text-3xl">×</span>
            </button>
            
            {selectedReviewImages.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 z-10"
                >
                  <ChevronLeft className="h-8 w-8" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 z-10"
                >
                  <ChevronRight className="h-8 w-8" />
                </button>
              </>
            )}
            
            <img
              src={selectedReviewImages[selectedImageIndex]}
              alt="Review image"
              className="max-w-full max-h-full object-contain rounded-lg"
            />
            
            {selectedReviewImages.length > 1 && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-sm">
                {selectedImageIndex + 1} of {selectedReviewImages.length}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}